if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 80));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        this.querySelectorAll('[data-es-gallery-step]').forEach(button => button.addEventListener('click', () => this.stepMedia(Number(button.dataset.esGalleryStep))));
        this.addEventListener('keydown', event => {
          if (!event.target.closest('button') || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
          event.preventDefault();
          this.stepMedia(event.key === 'ArrowLeft' ? -1 : 1);
        });
        this.mql.addEventListener('change', () => this.updateCompanion());
        this.updateCompanion();
      }

      prepareCrossfade(next) {
        const current = this.elements.viewer.querySelector('li[data-media-id].is-active');
        if (!this.mql.matches || !current || current === next || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        if (!current.querySelector('.product__modal-opener--image') || !next.querySelector('.product__modal-opener--image')) return;
        clearTimeout(this.fadeTimeout);
        this.querySelector('.es-gallery-outgoing')?.remove();
        const list = this.elements.viewer.querySelector('[id^="Slider-Gallery"]');
        const rect = list.getBoundingClientRect();
        const parentRect = this.elements.viewer.getBoundingClientRect();
        const overlay = list.cloneNode(true);
        overlay.querySelectorAll('li:not(.is-active):not(.is-companion)').forEach(item => item.remove());
        [overlay, ...overlay.querySelectorAll('*')].forEach(element => {
          element.removeAttribute('id');
          element.removeAttribute('data-media-id');
          element.removeAttribute('aria-controls');
        });
        overlay.classList.add('es-gallery-outgoing');
        overlay.setAttribute('aria-hidden', 'true');
        overlay.inert = true;
        Object.assign(overlay.style, {
          position: 'absolute', top: (rect.top - parentRect.top) + 'px',
          left: (rect.left - parentRect.left) + 'px', width: rect.width + 'px',
          height: rect.height + 'px', margin: '0', opacity: '1',
          zIndex: '4', pointerEvents: 'none', transition: 'opacity 360ms ease'
        });
        this.elements.viewer.appendChild(overlay);
        return () => requestAnimationFrame(() => requestAnimationFrame(() => {
          overlay.style.opacity = '0';
          this.fadeTimeout = setTimeout(() => overlay.remove(), 400);
        }));
      }

      stepMedia(step) {
        const items = Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));
        const index = items.findIndex(item => item.classList.contains('is-active'));
        const next = items[(Math.max(index, 0) + step + items.length) % items.length];
        if (next) this.setActiveMedia(next.dataset.mediaId, false);
      }

      updateCompanion(active) {
        const items = Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));
        active = active || items.find(item => item.classList.contains('is-active')) || items[0];
        if (!active) return;
        items.forEach(item => item.classList.remove('is-companion'));
        const index = items.indexOf(active);
        if (this.mql.matches && items.length > 1 && active.querySelector('.product__modal-opener--image')) {
          const next = items[(index + 1) % items.length];
          if (next.querySelector('.product__modal-opener--image')) next.classList.add('is-companion');
        }
        this.warmNearbyMedia(active);
        const counter = this.querySelector('.es-gallery-counter');
        if (counter) counter.textContent = (index + 1) + ' / ' + items.length;
      }

      warmNearbyMedia(active) {
        const items = Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));
        const index = items.indexOf(active);
        if (index < 0) return;
        [0, 1, 2, -1].forEach(offset => {
          const item = items[(index + offset + items.length) % items.length];
          const image = item.querySelector('.product__modal-opener--image img');
          if (!image) return;
          image.loading = 'eager';
          image.decoding = 'async';
          if (image.decode) image.decode().catch(() => {});
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.updateCompanion(event.detail.currentElement);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        const finishCrossfade = this.prepareCrossfade(activeMedia);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.updateCompanion(activeMedia);
        if (finishCrossfade) finishCrossfade();
        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        const announce = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        if (image.complete && image.naturalWidth) announce();
        else image.addEventListener('load', announce, { once: true });
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
